import storage from "../storage";
import { User as FirebaseUser, signOut } from "firebase/auth";
import { queryClient } from "@/config/query";
import { Organization, Brand, User, Product } from "@/types/entities";
import { auth } from "@/config/firebase";
import router from "@/router";
import { useFormStatus } from "@dippi/forms";
import { log } from "../logger";
import { useUser } from "@/context/user";
import { CustomClaims } from "@/types/utils";
import constant from "../constant";
import api from "@/config/api";

const setCurrentUser = async (auth: FirebaseUser) => {
    const login = useUser.getState().login;
    const savedRoute = sessionStorage.getItem("savedRoute");

    const pages = getAuthenticatedPages();
    const isOnAuthenticatedPage = Object.values(pages).some(i => i === true);

    const { organizations } = await api.get<User>("/users");
    const oids = Object.keys(organizations);
    const oid = storage.getItem("oid");
    const claims = await getClaims(auth);

    storage.setItem("hasMultipleOrgs", oids.length > 1);

    if (oids.length === 0) {
        login();
        return router.navigate("/organization");
    }

    if (savedRoute && claims) {
        const { resume } = await handleSavedRoutes(oids, claims);
        if (resume === false) return;
    }

    // If there is oid in Local Storage and in Firebase claims but they don't match OR oid exists in Firebase claims but not in Local Storage, then save Firebase claims oid to Local Storage and use it to fetch entities.
    if (
        (oid && claims.organization?.oid && oid !== claims.organization?.oid) ||
        (claims.organization?.oid && !oid)
    ) {
        // Update storage and set entities.
        storage.setItem("oid", claims.organization?.oid);
        await setEntities(claims.organization?.oid), login();

        // Default navigation if no saved route or mismatch.
        return router.navigate(`/organization/${claims.organization?.oid}/brands`);
    }

    if (oid && oids.includes(oid)) {
        // If there is oid in Local Storage but not in Firebase claims, then set Local Storage oid to Firebase claims.
        if (!claims.organization?.oid) await api.post("/users/set-claims", { data: { oid: oid } });

        await setEntities(oid), login();

        // Check if oid in current URL is different from the one that is in Local Storage. If they are different, then redirect user to a new URL with correct oid in it.
        const oidInCurrentURL = router.state.location.pathname
            .match(/organization\/([a-z0-9]+)/gi)?.[0]
            .split("/")[1];

        if (oidInCurrentURL && oidInCurrentURL !== oid) {
            return router.navigate(`/organization/${oid}/brands`);
        }

        if (isOnAuthenticatedPage) return;
        return router.navigate(`/organization/${oid}/brands`);
    }

    // If user doesn't have oid in Local Storage, neither in Firebase claims but user is part of more than one organizations then redirect to Select Organization page.
    if (!oid && !claims.organization?.oid && oids.length > 1) {
        login();
        return router.navigate("/organizations");
    }

    // If user doesn't have oid in Local Storage, neither in Firebase claims and user is part of just one organization, then automatically select that organization.
    if (!oid && !claims.organization?.oid && oids.length === 1 && oids[0]) {
        const oid = oids[0];
        await api.post("/users/set-claims", { data: { oid } });
        await setEntities(oid), storage.setItem("oid", oid);

        login();
        return router.navigate(`/organizations/${oid}/brands`);
    }
};

const handleSavedRoutes = async (
    oids: string[],
    claims: Partial<CustomClaims>,
): Promise<{ resume: boolean }> => {
    const savedRoute = sessionStorage.getItem("savedRoute");
    const login = useUser.getState().login;

    // Extract organization ID and brand ID from the saved route.
    const oid = savedRoute?.match(/organization\/([a-z0-9]+)/gi)?.[0].split("/")[1] as string;
    const bid = savedRoute?.match(/brands\/([a-z0-9]+)/i)?.[1];

    if (claims.organization?.oid && oid && oid === claims.organization.oid) {
        bid && storage.setItem("bid", bid);

        await setEntities(oid), login();
        router.navigate(savedRoute);

        return { resume: false };
    }

    if (oid && claims.organization?.oid && oid !== claims.organization?.oid && oids.includes(oid)) {
        bid && storage.setItem("bid", bid);

        await api.post("/users/set-claims", { data: { oid: oid } });

        storage.setItem("oid", oid);
        await setEntities(oid), login();
        router.navigate(savedRoute);

        return { resume: false };
    }

    return { resume: true };
};

const getClaims = async (user: FirebaseUser) => {
    const result = await user.getIdTokenResult();
    const claims = result.claims as Partial<CustomClaims>;

    return claims;
};

const setEntities = async (oid: string) => {
    const data = await api.get<Organization>(`/organizations/${oid}`);
    const brands = await api.get<Brand[]>(`/organizations/${oid}/brands`);

    queryClient.setQueryData(["organization", oid], data);
    queryClient.setQueryData(["brands", oid], brands);
};

const capitalize = (value: string | undefined) => {
    if (!value) return;

    return value.charAt(0).toUpperCase() + value.substring(1).toLowerCase();
};

const shorten = (name: string, maxLength: number) => {
    return name.length > maxLength ? name.substring(0, 35).trim() + "..." : name;
};

const getRoutes = () => {
    return {
        signIn: "/sign-in",
        organizations: "/organizations",
        organization: "/organization",
        organizationMembers: () => {
            const oid = storage.getItem("oid");

            return `/organization/${oid}/members`;
        },
        organizationSettings: () => {
            const oid = storage.getItem("oid");

            if (oid) return `/organization/${oid}/settings`;
            return "/organization";
        },
        profile: "/profile",
        brands: () => {
            const oid = storage.getItem("oid");
            if (oid) return `/organization/${oid}/brands`;
            return "/organization";
        },
        brandSettings: (id?: string) => {
            const oid = storage.getItem("oid");
            const bid = storage.getItem("bid") || id;

            return bid ? `/organization/${oid}/brand/${bid}` : `/organization/${oid}/brand`;
        },
        products: (id?: string) => {
            const oid = storage.getItem("oid");
            const bid = storage.getItem("bid");

            return `/organization/${oid}/brands/${bid ?? id}/products`;
        },
        productSettings: (typeName: Product["typeName"], pid?: string) => {
            const oid = storage.getItem("oid");
            const bid = storage.getItem("bid");

            return pid
                ? `/organization/${oid}/brands/${bid}/product/${typeName}/${pid}`
                : `/organization/${oid}/brands/${bid}/product/${typeName}`;
        },
        support: "/support",
    };
};

const getAuthenticatedPages = () => {
    const path = router.state.location.pathname;
    return {
        profile: /^\/profile$/.test(path),

        brands: /\/organization\/\w+\/brands$/.test(path),
        brandSettings: /^\/organization\/\w+\/brand(\/\w+)?$/.test(path),

        products: /^\/organization\/\w+\/brands\/\w+\/products$/.test(path),
        productSettings:
            /^\/organization\/\w+\/brands\/\w+\/product\/(\d{2,2}\.\d{2,2}\.\d{1,2})(\/\w+)?$/.test(
                path,
            ),

        support: /^\/support$/.test(path),

        members: /^\/organization\/\w+\/members$/.test(path),
        settings: /^\/organization\/\w+\/settings$/.test(path),
        organization: /^\/organization$/.test(path),
        organizations: /^\/organizations$/.test(path),
    };
};

const handleRouting = (route: string) => {
    const { savedRoute, isModalOpen } = useFormStatus.getState();

    if (isModalOpen && savedRoute) {
        return router.navigate(savedRoute);
    }

    router.navigate(route);
};

const checkEmailStatus = async (user: FirebaseUser) => {
    if (user.emailVerified === false) {
        await signOut(auth);
        throw new Error(`Your email address ${user.email} requires verification. $sentry-ignore`);
    }
};

const sleep = async (ms: number = 1000) => {
    await new Promise(resolve => setTimeout(resolve, ms));
};

const triggerFileDownload = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click(), link.remove();
};

const constructBrandKey = (title: string) => {
    const key = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/^[0-9]+/, "")
        .replace(/^-+/, "")
        .replace(/-+$/, "")
        .trim()
        .replace(/\s/g, "-");

    return key;
};

const onHandleLink = async (
    action: "open-eucompliant" | "open-marketing" | "copy-eucompliant" | "copy-marketing",
    eid: string,
) => {
    try {
        const link =
            constant.DEV || constant.STAGING
                ? `https://info-staging.dippi.app/p/${eid}`
                : `https://info.dippi.app/p/${eid}`;

        if (action === "open-eucompliant") {
            window.open(link + "?v=1", "_blank");
        }

        if (action === "open-marketing") {
            window.open(link, "_blank");
        }

        if (action === "copy-eucompliant") {
            await navigator.clipboard.writeText(link + "?v=1");
            log.success("EU compliant link copied!");
        }
        if (action === "copy-marketing") {
            await navigator.clipboard.writeText(link);
            log.success("Marketing link copied!");
        }
    } catch (e) {
        log.error(e, "useHandleLinks.onHandleLink");
    }
};

const SELECT_ITEMS = {
    industries: constant.INDUSTRIES.map(i => ({
        label: i.title,
        value: i.code,
    })),
    countries: Object.entries(constant.COUNTRIES).map(i => ({ label: i[1], value: i[0] })),
    business: constant.BUSINESS.map(i => ({
        label: i[0]!.toUpperCase() + i.substring(1),
        value: i,
    })),
};

export {
    getRoutes,
    getAuthenticatedPages,
    capitalize,
    handleRouting,
    shorten,
    setCurrentUser,
    setEntities,
    checkEmailStatus,
    sleep,
    triggerFileDownload,
    constructBrandKey,
    onHandleLink,
    SELECT_ITEMS,
};
